import { FunctionComponent, FocusEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useOnboardContext } from '../../context'
import UserRepository from 'modules/user/infra/repositories/UserRepository'
import HttpClient from 'shared/httpClients/frotista.api'
import {
  formatTelefoneMask,
  formatCep,
  formatOnlyNumbers,
} from 'shared/handlers/TextFieldHandler'
import {
  removeCaracteresEspeciais,
  cnpjMask,
  cepMask,
  normalizeCnpj,
  phoneMask,
} from 'shared/utils/formatters'

import { steps } from 'modules/onboarding'

import {
  Container,
  Header,
  Action,
  Form,
  Button,
  Heading,
  TextField,
  Typography,
} from './styles'

import { Onboard } from 'modules/onboarding/types/Onboard'

type TProps = {
  setSelectedStep?(step: number): void
}

type TypeForm = {
  cnpj: string
  antt: string
  email: string
  celular: string
  cep: string
  logradouro: string
  numero: string
  complemento?: string
  cidade: string
  bairro: string
  estado: string
}

const PATTERN_EMAIL = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

const userRepository = new UserRepository()
const api = new HttpClient().api()

const Cadastro: FunctionComponent<TProps> = ({ setSelectedStep }) => {
  const { state, dispatch } = useOnboardContext()

  const [loadingCep, setLoadingCep] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<TypeForm>()

  const onHandleSubmit = async (data: TypeForm) => {
    const {
      cnpj,
      antt,
      email,
      celular,
      cep,
      logradouro,
      numero,
      complemento,
      cidade,
      bairro,
      estado,
    } = data

    const cadastro: Onboard = {
      ...state,
      antt,
      email,
      cnpj: normalizeCnpj(cnpj),
      telefone: removeCaracteresEspeciais(celular),
      endereco: {
        ...state.endereco,
        logradouro,
        cep: removeCaracteresEspeciais(cep),
        numero,
        complemento,
        bairro,
        cidade,
        estado,
      },
    }
    dispatch(cadastro)
    setSelectedStep(steps.SelecionaDoc)
  }

  const handleOnBlurCep = async (e: FocusEvent<HTMLInputElement>) => {
    const cepValue = removeCaracteresEspeciais(e.target.value)
    if (cepValue?.length < 8) {
      return
    }
    try {
      clearErrors('cep')
      setLoadingCep(true)
      await userRepository.consultZipCode({ cep: cepValue }).then(dados => {
        dispatch({
          ...state,
          endereco: {
            ...state.endereco,
            cidade: dados.cidade,
            estado: dados.estado,
            bairro: dados.bairro,
            logradouro: dados.logradouro,
            cidade_ibge: dados.cidade_ibge,
          },
        })

        setValue('cidade', dados.cidade)
        setValue('estado', dados.estado)
        setValue('bairro', dados.bairro)
        setValue('logradouro', dados.logradouro)
      })
    } catch (err) {
      setError('cep', { message: 'CEP invalido' })
    }
    setLoadingCep(false)
  }

  const validateAntt = async (antt: string): Promise<boolean> => {
    try {
      clearErrors('antt')
      await api.get(`/parceiros/validate/${antt}/antt`)
      return true
    } catch (error) {
      setError('antt', { message: 'ANTT já cadastrada' })
      return false
    }
  }

  const validatePhone = async (phone: string): Promise<boolean> => {
    try {
      clearErrors('celular')
      const phoneValue = removeCaracteresEspeciais(phone)
      await api.get(`/parceiros/validate/${phoneValue}/phone`)
      return true
    } catch (error) {
      setError('celular', {
        message: 'Número já cadastrado',
      })
      return false
    }
  }

  return (
    <Container>
      <Header />
      <Form onSubmit={handleSubmit(onHandleSubmit)} id="cadForm">
        <div>
          <Heading variant="h3">Informações de cadastro</Heading>
          <Typography variant="p1">
            Faça a conferência de todos os dados para prosseguir com o cadastro.
          </Typography>
        </div>
        <div>
          <Heading variant="h4">Empresa</Heading>
          <TextField
            label="CNPJ"
            length={30}
            minLength={14}
            maxLength={18}
            required
            defaultValue={cnpjMask(state?.cnpj)}
            disabled
            {...register('cnpj', {
              required: true,
              value: cnpjMask(state?.cnpj),
            })}
          />
          <TextField
            label="ANTT"
            length={20}
            required
            maxLength={9}
            onKeyUp={formatOnlyNumbers}
            defaultValue={state?.antt}
            error={errors?.antt && true}
            supportingText={
              errors?.antt?.type === 'validate'
                ? 'ANTT já cadastrada'
                : errors?.antt?.message
            }
            {...register('antt', {
              required: true,
              minLength: {
                value: 9,
                message: 'Minímo de 9 caracteres',
              },
              validate: validateAntt,
            })}
          />
          <TextField
            label="E-mail"
            length={50}
            required
            defaultValue={state?.email}
            disabled
            {...register('email', {
              required: true,
              value: state?.email,
            })}
          />
          <TextField
            label="Celular"
            length={30}
            required
            maxLength={15}
            minLength={14}
            onKeyUp={formatTelefoneMask}
            error={errors?.celular && true}
            supportingText={
              errors?.celular?.type === 'validate'
                ? 'Número já cadastrado'
                : errors?.celular?.message
            }
            defaultValue={phoneMask(state?.telefone)}
            {...register('celular', {
              required: true,
              minLength: {
                value: 14,
                message: 'Insira um número valido',
              },
              maxLength: {
                value: 15,
                message: 'Insira um número valido',
              },
              validate: validatePhone,
            })}
          />
        </div>
        <div>
          <Heading variant="h4">Endereço</Heading>
          <TextField
            label="CEP"
            length={30}
            required
            maxLength={8}
            minLength={8}
            onKeyUp={formatCep}
            onBlurCapture={handleOnBlurCep}
            error={errors?.cep && true}
            supportingText={errors?.cep?.message}
            defaultValue={cepMask(state?.endereco?.cep)}
            {...register('cep', {
              required: true,
              minLength: {
                value: 8,
                message: 'Insira um cep valido',
              },
            })}
          />
          <TextField
            label="Rua"
            length={50}
            required
            defaultValue={state?.endereco?.logradouro}
            disabled={loadingCep}
            {...register('logradouro', {
              required: true,
            })}
          />
          <TextField
            label="Número"
            length={10}
            maxLength={12}
            required
            defaultValue={state?.endereco?.numero}
            {...register('numero', {
              required: true,
            })}
          />
          <TextField
            label="Cidade"
            required
            length={40}
            defaultValue={state?.endereco?.cidade}
            disabled={loadingCep}
            {...register('cidade', { required: true })}
          />
          <TextField
            label="Estado"
            required
            length={30}
            defaultValue={state?.endereco?.estado}
            disabled={loadingCep}
            {...register('estado', { required: true })}
          />
          <TextField
            label="Bairro"
            required
            length={50}
            defaultValue={state?.endereco?.bairro}
            disabled={loadingCep}
            {...register('bairro', {
              required: true,
            })}
          />
          <TextField
            label="Complemento"
            length={60}
            defaultValue={state?.endereco?.complemento}
            {...register('complemento')}
          />
        </div>
      </Form>
      <Action>
        <Button
          type="button"
          variant="contained"
          size="lg"
          onClick={() => setSelectedStep(steps.Terms)}
        >
          Voltar
        </Button>
        <Button type="submit" variant="contained" size="lg" form="cadForm">
          Próximo
        </Button>
      </Action>
    </Container>
  )
}

export default Cadastro
